import ConsentManager from '../ConsentManager';

/**
 * ConsentManager.Tag
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @package felixkiesewetter-js/consentmanager
 * @lastModified 2025-07-11 Added disable() method
 * @since 5.0.0
 */
class Tag
{

    // Private
    #callback :Function;
    #category :null|string;
    #consent :boolean = false;
    #consentManager :ConsentManager;
    #description :string;
    #label :string = '';
    #reload :boolean = true;
    #uri :string = '';

    // Protected
    _cookies :string[] = [];
    _name :string = '';
    _readonly :boolean = false;
    _required :boolean = false;
    _tagName :string = '*';

    /**
     * Tag constructor
     *
     * @param {Object} options
     * @returns {Tag}
     */
    constructor(options :object)
    {
        this.#mergeOptions(options);
        return this;
    }

    /**
     * Initialise the tag
     *
     * @param {null|ConsentManager} consentManager
     * @returns {Tag}
     */
    init(consentManager :null|ConsentManager = null) :Tag
    {
        consentManager && this.setConsentManager(consentManager);
        if (!this.getConsentManager()) {
            this.getConsentManager().getDebugger().error('requires a consent manager parent');
            return this;
        }
        if (this.getConsent(true)) {
            this.optIn();
        }
        else {
            this.disable();
            if (this._cookies) {
                this.#checkCookies();
            }
        }
        return this;
    }

    // ###############
    // Private methods
    // ###############

    /**
     * Check if document contains cookies of property.
     */
    #checkCookies() :Tag
    {
        if (this._cookies) {
            Object.values(this._cookies).forEach(propertyCookie => {
                if (propertyCookie.regex || 'undefined' === typeof propertyCookie.regex) {
                    Object.keys(this.#getCookies()).forEach(cookieName => {
                        if (new RegExp(propertyCookie.name, 'g').test(cookieName)) {
                            this.#clearCookie(cookieName, propertyCookie.domain, propertyCookie.path)
                        }
                    })
                }
                // TODO: no other implementation than regex: true so far ...
            })
        }
        return this;
    }

    /**
     * Clear cookie.
     */
    #clearCookie(name, domain = null, path = null) :Tag
    {
        if ('undefined' === typeof this.#getCookies()[name]) {
            return this;
        }
        if (!domain) {
            domain = this.getConsentManager().getCookieDomain();
        }
        if (!path) {
            path = '/';
        }
        const cookie :string = `${name}= ;expires= Thu, 01 Jan 1970 00:00:00 GMT; path= ${path}; domain= ${domain}`;
        document.cookie = cookie;
        this.getConsentManager().getDebugger().warn(`${this.constructor.name}.#clearCookie()`, name);
        return this;
    }

    /**
     * Merge tag options.
     */
    #mergeOptions(options :object) :Tag
    {
        this._name = options.name || this._name;
        this.setLabel(options.label);
        this.#description = options.description || '';
        this.#uri = options.uri || this.#uri;
        this._readonly = options.readonly || options.required ? true : this._readonly;
        this._required = options.required || this._required;
        this.#reload = options.reload || this.#reload;
        this._cookies = options.cookies || this._cookies;
        this.#callback = options.callback || this.#callback;
        this.#category = options.category || null;
        return this;
    }

    // ##############
    // Public methods
    // ##############

    /**
     * Allow this tag (give consent).
     */
    public allow() :Tag
    {
        if (this.getConsent()) {
            return this;
        }
        return this.optIn().setConsent(true);
    }

    /**
     * Disable HTML tags for which consent is managed.
     */
    public disable() :Tag
    {
        document.querySelectorAll(`${this._tagName}[data-consent="${this._name}"]`)?.forEach((element :HTMLElement) => {
            if (element.src) {
                element.setAttribute('data-src', element.src);
                element.removeAttribute('src');
            }
            if ('SCRIPT' === element.tagName || 'LINK' === element.tagName || 'STYLE' === element.tagName) {
                element.setAttribute('type', 'text/plain');
            }
            const consentInfo :HTMLParagraphElement = document.createElement('p');
            consentInfo.classList.add('consent-manager__info');
            consentInfo.innerHTML = `${
                this
                    .getConsentManager()
                    .getTranslator()
                    .get('notice_consent_required')
                    .replace('%1', this.getLabel())
                    .replace('%2', `<a href="${this.getUri()}" target="_blank">${this.getConsentManager().getTranslator().get('label_privacy_link')}</a>`)
            }<button data-consent="${this.getName()}" rel="consent-manager">Datenschutzeinstellungen</button>`;
            element.parentNode.insertBefore(consentInfo, element.nextSibling);
        });
        return this;
    }

    /**
     * Disallow this tag (revoke consent).
     */
    public disallow() :Tag
    {
        if (!this.getConsent()) {
            return this;
        }
        return this.optOut().setConsent(false);
    }

    /**
     * Opt-in tag.
     */
    public optIn() :Tag
    {
        if (this._required) {
            return this;
        }
        this.getConsentManager().getDebugger().log(this._name);
        if (this.#callback && Function === this.#callback.constructor) {
            this.#callback(true);
        }
        return this;
    }

    /**
     * Opt-out property
     *
     * @returns {Tag}
     */
    public optOut()
    {
        this.getConsentManager().getDebugger().log(this._name);
        if (this.#reload) {
            this.getConsentManager().queuePageReload();
        }
        if (this.#callback && Function === this.#callback.constructor) {
            this.#callback(false);
        }
        return this;
    }

    // #######
    // Setters
    // #######

    /**
     * Setter for the consent
     *
     * @param {boolean} consent
     * @returns {Tag}
     */
    public setConsent(consent :boolean) :Tag
    {
        this.#consent = consent;
        return this;
    }

    /**
     * Setter for the consent manager object
     *
     * @param {ConsentManager} consentManager
     * @returns {Tag}
     */
    public setConsentManager(consentManager :ConsentManager) :Tag
    {
        this.#consentManager = consentManager;
        return this;
    }

    /**
     * Set callback
     *
     * @param {Function} callback
     * @returns {Tag}
     */
    public setCallback(callback)
    {
        if (!callback || Function !== callback.constructor) {
            return this
        }
        this.#callback = callback
        return this
    }

    /**
     * Set the category.
     *
     * @param category
     */
    public setCategory(category :string) :Tag
    {
        this.#category = category;
        return this;
    }

    /**
     * Set property label
     *
     * @param {String|null} label
     * @returns {Tag}
     */
    setLabel(label)
    {
        if (label) {
            this.#label = label;
            return this;
        }
        this.#label = this.getConsentManager()?.getTranslator().get(`property_${this._name}`);
        return this;
    }

    // TODO: maybe getCookies(), checkCookies() and clearCookie() should rather be functions of this.#consentManager???

    // #######
    // Getters
    // #######

    /**
     * Getter for the category.
     */
    public getCategory() :string
    {
        return this.#category;
    }

    /**
     * Getter for the current consent status
     *
     * @param {boolean} onInitialisation
     * @returns {boolean}
     */
    public getConsent(onInitialisation = false) :boolean
    {
        if (this._required) {
            return true;
        }
        if (onInitialisation) {
            let consent = false;
            const consentManagerStorage = this.getConsentManager().getStorage();
            if (
                !consentManagerStorage.hasOwnProperty('consent') ||
                !(this._name in consentManagerStorage.consent)
            ) {
                this.getConsentManager().setRequireUserInteraction(true);
            }
            else {
                consent = consentManagerStorage.consent[this.getName()];
            }
            this.setConsent(consent);
            return this.#consent;
        }
        return this.#consent;
    }

    /**
     * Get this tag's assigned consent manager instance
     *
     * @returns {ConsentManager}
     */
    public getConsentManager() :ConsentManager
    {
        return this.#consentManager;
    }

    /**
     * Getter for this tag's description
     */
    public getDescription() :string
    {
        return this.#description;
    }

    /**
     * Get this tag's label
     *
     * @returns {string}
     */
    public getLabel() :string
    {
        if (Object === this.#label.constructor) {
            return this.#label[this.getConsentManager().getTranslator().getLanguage()] || '/';
        }
        return this.#label;
    }

    /**
     * Get this tag's name
     *
     * @returns {string}
     */
    public getName() :string
    {
        return this._name;
    }

    /**
     * Get this tag's uri
     *
     * @returns {String}
     */
    public getUri() :string
    {
        if (Object === this.#uri.constructor) {
            return this.#uri[this.getConsentManager().getTranslator().getLanguage()] || '/';
        }
        return this.#uri;
    }

    /**
     * Get document cookies
     *
     * @returns {null|object}
     */
    #getCookies() :null|object
    {
        let cookies :object = {};
        document.cookie.split(';').forEach((cookie, c) => {
            if (!cookie) {
                return null;
            }
            cookies[cookie.split('=')[0].trim()] = cookie.split('=')[1].trim();
        });
        return cookies;
    }

    /**
     * Check if property is read-only
     *
     * @returns {boolean}
     */
    public isReadonly()
    {
        return this._readonly;
    }

}

export default Tag;
